import { useEffect, useState, useMemo, useRef, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  clearAnalytics,
  fetchAnalytics,
  selecthasMoreAnalytics,
  selectAnalytics,
  selectAnalyticsLoading,
} from "../../../../store/slices/task-analytics";
import { TableCell } from "../../../../components/table/table-cell/table-cell";
import AuditAnalyticsHeader from "../audit-analytics-header";
import "../../styles.css";
import InfinityTable from "../../../../components/infinity-table/infinity-table";
import moment from "moment";
import { Body } from "../../../../components/main-body/body";
import classNames from "classnames";
import "../../../../components/main-body/body.css";
import { Progress } from "antd";
import ApiSubjectsManagement from "../../../../api/subjects-manager";
import ApiSavedViews from "../../../../api/saved-views";
import AnalyticsCard from "./analytics-card/analytics-card";
import { useLocation } from "react-router-dom";
import { useProjectNavigate } from "../../../../common/utils/useProjectNavigate";
import ApiSubjectsDashboard from "../../../../api/subjects-dashboard";
import ApiArticles from "../../../../api/articles";
import ApiPlanManagement from "../../../../api/plan-management";
import encodeSvgWithColor from "../../../../common/utils/encodeSvgWithColor";
import { selectSettings } from "../../../../store/slices/settings";
import { selectCurrentProject } from "../../../../store/slices/user";

const sortViews = (list = []) =>
  [...list].sort((a, b) => {
    if (Boolean(a.isDefault) !== Boolean(b.isDefault)) {
      return a.isDefault ? -1 : 1;
    }

    return String(a.name || "").localeCompare(String(b.name || ""));
  });

const normalizeSorting = (sorting) => ({
  sortField: sorting?.sortField || null,
  sortOrder: sorting?.sortOrder || null,
});

const normalizeEffectiveFilters = (filters = {}) =>
  Object.entries(filters || {}).reduce((acc, [key, value]) => {
    if (Array.isArray(value)) {
      if (value.length > 0) {
        acc[key] = value;
      }
      return acc;
    }

    if (value !== null && value !== undefined && value !== "") {
      acc[key] = value;
    }

    return acc;
  }, {});

// Mirrors the content/tasks task-type filter (regular vs survey).
const TASK_TYPE_OPTIONS = [
  { id: "regular", name: "Regular" },
  { id: "survey", name: "Survey" },
];

const buildFetchKey = (query, filters) =>
  JSON.stringify({
    query: query || "",
    filters: normalizeEffectiveFilters(filters),
  });

const TasksAnalyticsTable = ({ navigation }) => {
  const analytics = useSelector(selectAnalytics);
  const hasMore = useSelector(selecthasMoreAnalytics);
  const isLoading = useSelector(selectAnalyticsLoading);
  const settings = useSelector(selectSettings);
  const currentProject = useSelector(selectCurrentProject);

  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useProjectNavigate();
  const location = useLocation();

  const defaultFiltersRef = useRef({
    period: [
      {
        start: moment().subtract(30, "days").utc().toISOString(),
        end: moment().utc().toISOString(),
      },
    ],
  });

  const defaultSortingRef = useRef({
    sortField: null,
    sortOrder: null,
  });
  const lastFetchKeyRef = useRef(null);

  const sortingOptions = [
    { value: "title", label: t("analytics.table.task") },
    { value: "lastCompletedAt", label: t("analytics.table.last_completed") },
    { value: "plans", label: t("analytics.table.plan") },
    { value: "completionRate", label: t("analytics.table.adherence") },
  ];

  const defaultFilters = defaultFiltersRef.current;
  const defaultSorting = defaultSortingRef.current;

  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState(null);
  const [filtersReady, setFiltersReady] = useState(false);
  const [sorting, setSorting] = useState(defaultSorting);

  const [subjectsList, setSubjectsList] = useState([]);
  const [searchValue, setSearchValue] = useState("");
  const [isOpen, setOpen] = useState(false);
  const [selectedAnalytics, setSelectedAnalytics] = useState({});
  const [cohortsList, setCohortsList] = useState([]);
  const [categoriesList, setCategoriesList] = useState([]);
  const [plansList, setPlansList] = useState([]);
  const [sitesList, setSitesList] = useState([]);

  const [savedViewsItems, setSavedViewsItems] = useState([]);
  const [savedViewsLoading, setSavedViewsLoading] = useState(false);
  const [selectedViewId, setSelectedViewId] = useState(null);

  const isSitesEnabled =
    settings?.find((s) => s.key === "sitesEnable")?.value === true;

  const SAVED_VIEW_SCOPE = "analytics_tasks";

  const safeFilters = filters || defaultFilters;
  const safeSorting = normalizeSorting(sorting);

  const handleNavigate = (path) => {
    if (location.pathname !== path) {
      navigate(path);
    }
  };

  const getDashboardFilterItems = () => {
    ApiSubjectsDashboard.filterField().then((r) => {
      setCohortsList(
        r.data.cohorts?.map((c) => ({ id: c.id, name: c.value })) || []
      );
      setSitesList(
        r.data.sites?.map((c) => ({ id: c.id, name: c.name })) || []
      );
    });
  };

  const handleSelectAnalytics = (task) => {
    handleNavigate(`/analytics/monitoring/tasks/${task.id}`);
    setSelectedAnalytics(task);
    setOpen(true);
  };

  const closeTaskDrawer = () => {
    handleNavigate(`/analytics/monitoring/tasks`);
    setOpen(false);
    setSelectedAnalytics({});
  };

  const fetchData = (
    currentPage = page,
    query = searchValue,
    currentFilters = safeFilters,
    reset = false
  ) => {
    if (!currentFilters) return;

    if (reset) {
      dispatch(clearAnalytics());
    }

    dispatch(
      fetchAnalytics({
        query,
        page: currentPage,
        ...currentFilters,
        createdAtEnd:
          currentFilters.period?.[currentFilters.period.length - 1]?.end,
        createdAtStart:
          currentFilters.period?.[currentFilters.period.length - 1]?.start,
      })
    );

    setPage(currentPage + 1);
  };

  const loadSavedViews = useCallback(async () => {
    if (!currentProject?.id) {
      setSavedViewsItems([]);
      setSelectedViewId(null);
      setFilters(defaultFilters);
      setSorting(defaultSorting);
      setFiltersReady(true);
      return;
    }

    setSavedViewsLoading(true);

    try {
      const { data } = await ApiSavedViews.getSavedViews({
        projectId: currentProject.id,
        scope: SAVED_VIEW_SCOPE,
      });

      const list = sortViews(data?.savedViews || []);
      setSavedViewsItems(list);

      const defaultView = list.find((item) => item.isDefault);

      if (defaultView) {
        setSelectedViewId(defaultView.id);
        setFilters(defaultView.filters || {});
        setSorting(normalizeSorting(defaultView.sorting));
      } else {
        setSelectedViewId(null);
        setFilters(defaultFilters);
        setSorting(defaultSorting);
      }
    } catch (error) {
      console.error("Failed to load saved views", error);
      setSavedViewsItems([]);
      setSelectedViewId(null);
      setFilters(defaultFilters);
      setSorting(defaultSorting);
    } finally {
      setSavedViewsLoading(false);
      setFiltersReady(true);
      setPage(1);
    }
  }, [currentProject?.id, defaultFilters, defaultSorting]);

  const handleUpdateSearchValue = (value) => {
    setSearchValue(value);
    setPage(1);
    fetchData(1, value, safeFilters, true);
  };

  const fetchSubjectList = () => {
    ApiSubjectsManagement.getSubjectsSimple().then((r) => {
      setSubjectsList(
        (r.data.subjects || []).map((s) => ({
          id: s.id,
          name: s.displayId,
        }))
      );
    });
  };

  const fetchCategories = async () => {
    const response = await ApiArticles.getCategories();
    if (response.data?.categories) {
      setCategoriesList(response.data.categories);
    }
  };

  const fetchPlans = async () => {
    const response = await ApiPlanManagement.getPlanList();
    if (response.data?.plans) {
      setPlansList(
        response.data.plans.map((p) => ({
          id: p.id,
          name: p.title,
        }))
      );
    }
  };

  const updateFilterValue = (filter) => {
    setFilters(filter);
    setPage(1);
  };

  const resetFilters = () => {
    setSelectedViewId(null);
    setFilters(defaultFilters);
    setSorting(defaultSorting);
    setPage(1);
  };

  const applySavedViewFilters = (viewState, viewId = null) => {
    setSelectedViewId(viewId);
    setFilters(
      viewState?.filters && Object.keys(viewState.filters).length
        ? viewState.filters
        : {}
    );
    setSorting(normalizeSorting(viewState?.sorting));
    setPage(1);
  };

  useEffect(() => {
    const pathParts = location.pathname.split("/");
    const taskId = pathParts[pathParts.length - 1];

    if (taskId && taskId !== "tasks") {
      const foundTask = analytics.find((task) => task.id === taskId);

      if (foundTask) {
        setSelectedAnalytics(foundTask);
      } else {
        setSelectedAnalytics({ id: taskId });
      }
      setOpen(true);
    } else {
      setOpen(false);
      setSelectedAnalytics({});
    }
  }, [location.pathname, analytics]);

  useEffect(() => {
    fetchCategories();
    fetchPlans();
    fetchSubjectList();
    getDashboardFilterItems();
  }, []);

  useEffect(() => {
    loadSavedViews();
  }, [loadSavedViews]);

  useEffect(() => {
    if (!filtersReady || !filters) return;

    const fetchKey = buildFetchKey(searchValue, filters);
    if (lastFetchKeyRef.current === fetchKey) return;
    lastFetchKeyRef.current = fetchKey;

    dispatch(clearAnalytics());
    fetchData(1, searchValue, filters, true);
  }, [filtersReady, filters]);

  const sortedAnalytics = useMemo(() => {
    const list = Array.isArray(analytics) ? [...analytics] : [];
    const { sortField, sortOrder } = safeSorting || {};

    if (!sortField || !sortOrder) {
      return list;
    }

    const dir = sortOrder === "ASC" ? 1 : -1;

    const getComparableValue = (item) => {
      switch (sortField) {
        case "title":
          return String(item.title || "").toLowerCase();

        case "lastCompletedAt":
          return item.lastCompletedAt
            ? moment(item.lastCompletedAt).valueOf()
            : 0;

        case "plans":
          return String(item.plans || "").toLowerCase();

        case "completionRate":
          return item.adherence || 0;

        default:
          return "";
      }
    };

    list.sort((a, b) => {
      const aVal = getComparableValue(a);
      const bVal = getComparableValue(b);

      if (aVal < bVal) return -1 * dir;
      if (aVal > bVal) return 1 * dir;
      return 0;
    });

    return list;
  }, [analytics, safeSorting]);

  const analyticsCardFilters = useMemo(
    () => ({
      period: safeFilters?.period,
      cohortIds: safeFilters?.cohortIds,
    }),
    [safeFilters]
  );

  const columns = [
    {
      title: t("analytics.table.task"),
      dataIndex: "title",
      sorter: true,
      render: (_, analyticsItem) => {
        const { avatar, avatarColor, backgroundColor, title } = analyticsItem;

        return (
          <TableCell
            img={avatar ? encodeSvgWithColor(avatar, avatarColor) : " "}
            imgStyle={{
              backgroundColor: backgroundColor,
              backgroundSize: "50%",
            }}
            title={title}
            subtext={analyticsItem?.Category?.name}
          />
        );
      },
      key: "title",
      width: "40%",
    },
    {
      title: t("analytics.table.last_completed"),
      dataIndex: "lastCompletedAt",
      sorter: true,
      render: (_, analyticsItem) => (
        <span>
          {analyticsItem.lastCompletedAt
            ? `${moment(analyticsItem.lastCompletedAt)
                .utc()
                .format("DD MMM YYYY, HH:mm")}, UTC`
            : "N/A"}
        </span>
      ),
      key: "lastCompletedAt",
      width: "20%",
    },
    {
      title: t("analytics.table.plan"),
      dataIndex: "plans",
      sorter: true,
      render: (_, analyticsItem) => <span>{analyticsItem.plans}</span>,
      key: "plans",
      width: "13%",
    },
    {
      title: t("analytics.table.type"),
      dataIndex: "taskType",
      render: (_, analyticsItem) => (
        <span>
          {analyticsItem.taskType === "survey"
            ? "Survey"
            : analyticsItem.taskType === "regular"
              ? "Regular"
              : "N/A"}
        </span>
      ),
      key: "taskType",
      width: "12%",
    },
    {
      title: t("analytics.table.adherence"),
      dataIndex: "completionRate",
      sorter: true,
      render: (_, analyticsItem) => (
        <span style={{ display: "flex", alignItems: "center", gap: "8px" }}>
          <Progress
            type="circle"
            percent={analyticsItem.adherence || 0}
            size={25}
            strokeLinecap="butt"
            strokeWidth={25}
            strokeColor="#2D7FFA"
            railColor="rgba(45, 127, 250, 0.2)"
            format={() => ""}
          />
          {analyticsItem.adherence || 0}%
        </span>
      ),
      key: "completionRate",
      width: "15%",
    },
  ];

  const handleTableChange = (pagination, tableFilters, sorter) => {
    const nextSortField = sorter?.field || null;
    const nextSortOrder = !sorter?.order
      ? null
      : sorter.order === "ascend"
        ? "ASC"
        : "DESC";

    setSorting({
      sortField: nextSortField,
      sortOrder: nextSortOrder,
    });
  };

  const filterItems = [
    { name: "Category", id: "categoryIds", children: categoriesList },
    {
      name: "Date peroid",
      id: "period",
      period: true,
      defaultValue: defaultFilters.period,
    },
    { name: "Plan", id: "planIds", children: plansList },
    { name: "Task type", id: "taskTypes", children: TASK_TYPE_OPTIONS },
    { name: "Cohort", id: "cohortIds", children: cohortsList },
    ...(isSitesEnabled
      ? [{ name: "Sites", id: "siteIds", children: sitesList }]
      : []),
  ];

  const headerProps = useMemo(
    () => ({
      search: {
        value: searchValue,
        onChange: handleUpdateSearchValue,
        placeholder: t("assets.btn.search"),
      },
    }),
    [searchValue]
  );

  return (
    <Body disabledPaddings={true}>
      <AuditAnalyticsHeader
        title={t("analytics.tasks")}
        filterItems={filterItems}
        updateFilterValue={updateFilterValue}
        header={headerProps}
        filters={safeFilters}
        sortingOptions={sortingOptions}
        savedViews={{
          enabled: true,
          projectId: currentProject?.id,
          scope: SAVED_VIEW_SCOPE,
          filters: safeFilters,
          sorting: safeSorting,
          defaultFilters,
          defaultSorting,
          filterItems,
          items: savedViewsItems,
          loading: savedViewsLoading,
          selectedViewId,
          onSelectedViewChange: setSelectedViewId,
          onApplySavedView: applySavedViewFilters,
          onResetFilters: resetFilters,
          onReloadSavedViews: loadSavedViews,
          disabled: isLoading || savedViewsLoading,
        }}
      />

      <div className={classNames("lf-content")}>
        <InfinityTable
          data={sortedAnalytics}
          fetchData={() => fetchData(page, searchValue, safeFilters)}
          hasMore={hasMore}
          tableColumns={columns}
          isLoading={isLoading}
          before={navigation}
          onHandleRowClick={handleSelectAnalytics}
          onChange={handleTableChange}
        />
      </div>

      <AnalyticsCard
        analytics={selectedAnalytics}
        isOpen={isOpen}
        close={closeTaskDrawer}
        subjects={subjectsList}
        cohorts={cohortsList}
        initialFilters={analyticsCardFilters}
      />
    </Body>
  );
};

export default TasksAnalyticsTable;
